import { Client } from "lib/wts.client"

export interface FavoriteItem {
    displayPath: string
    sourceProvider: string
    sourceId: string
    sourceLink: string
    newWindow: boolean
    shortcut: number
}

type Listener = (items: FavoriteItem[]) => void

class FavoritesService {
    private cache: FavoriteItem[] | undefined
    private listeners: Listener[] = []

    async load(force = false): Promise<FavoriteItem[]> {
        if (!force && this.cache) return this.cache
        try {
            const resp = await Client.get<any>("WTSSYSTEM.FAVORITES.Load", {}, { casing: "upper" })

            const raw = (resp?.value ?? []) as any[]
            this.cache = raw.map(r => ({
                displayPath:    String(r.DISPLAY_PATH ?? ""),
                sourceProvider: String(r.SOURCE_PROVIDER ?? ""),
                sourceId:       String(r.SOURCE_ID ?? ""),
                sourceLink:     String(r.SOURCE_LINK ?? ""),
                newWindow:      Boolean(r.NEW_WINDOW ?? false),
                shortcut:       Number(r.SHORTCUT ?? 0),
            })).filter(i => i.displayPath || i.sourceLink)
        } catch (e) {
            console.warn("FavoritesService.load failed", e)
            this.cache = []
        }
        this.emit()
        return this.cache
    }

    async save(items: FavoriteItem[]): Promise<void> {
        const payload = items.map(i => ({
            DISPLAY_PATH:    i.displayPath,
            SOURCE_PROVIDER: i.sourceProvider,
            SOURCE_ID:       i.sourceId,
            SOURCE_LINK:     i.sourceLink,
            NEW_WINDOW:      i.newWindow,
            SHORTCUT:        i.shortcut,
        }))

        await Client.post("WTSSYSTEM.FAVORITES.Save", { ITEMS: payload })
        this.cache = items.slice()
        this.emit()
    }

    async add(item: FavoriteItem): Promise<void> {
        const current = this.cache ?? await this.load()
        if (current.some(i => i.displayPath === item.displayPath && i.sourceLink === item.sourceLink))
            return
        await this.save([...current, item])
    }

    async removeAt(index: number): Promise<void> {
        const current = (this.cache ?? []).slice()
        if (index < 0 || index >= current.length) return
        current.splice(index, 1)
        await this.save(current)
    }

    async rename(index: number, newDisplayPath: string): Promise<void> {
        const current = (this.cache ?? []).slice()
        if (index < 0 || index >= current.length) return
        current[index] = { ...current[index], displayPath: newDisplayPath }
        await this.save(current)
    }

    async move(index: number, newFolder: string): Promise<void> {
        const current = (this.cache ?? []).slice()
        if (index < 0 || index >= current.length) return
        const leaf = current[index].displayPath.split("\\").pop() ?? current[index].displayPath
        const path = newFolder ? `${newFolder}\\${leaf}` : leaf
        current[index] = { ...current[index], displayPath: path }
        await this.save(current)
    }

    get items(): FavoriteItem[] {
        return this.cache ?? []
    }

    onChange(fn: Listener): () => void {
        this.listeners.push(fn)
        return () => {
            const idx = this.listeners.indexOf(fn)
            if (idx >= 0) this.listeners.splice(idx, 1)
        }
    }

    private emit() {
        for (const fn of this.listeners) {
            try { fn(this.cache ?? []) } catch (e) { console.error(e) }
        }
    }
}

export const favoritesService = new FavoritesService()
